import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import dayjs from 'dayjs';
import { action } from 'mobx';
import { observer } from 'mobx-react-lite';
import { ReactNode, useCallback } from 'react';
import { Fragrance } from '~/api/api.declaration';
import { FragranceService } from '~/service/service.fragrance';
import { UIService } from '~/service/service.ui';
import { ASINType, FragranceUpdateDto } from '~/shared/dto/admin/fragrance.dto';
import { Form } from '~/shared/form';
import { useService } from '~/shared/service/service.base';
import { AsyncView } from '../view.async';
import { Col, Row, Text } from '../view.box';

const sortOrder: Record<ASINType, number> = {
	PRIMARY: 0,
	MANUAL: 1,
	VARIATION: 2,
	BLACKLIST: 3,
	POTENTIAL: 4,
};

export const FragranceASINsDialog = observer<{ form: Form<FragranceUpdateDto, Fragrance>; reload: () => void }>(
	({ form, reload }) => {
		const [ui, fragSvc] = useService(UIService, FragranceService);
		const open = ui.dialog === FragranceASINsDialog;
		const id = form.initial?.id;

		const handleRefetch = useCallback(() => {
			id && fragSvc.refreshASIN(id, reload);
		}, [id, fragSvc, reload]);

		return (
			<Dialog open={open} maxWidth="lg" fullWidth onClose={ui.hideDialog}>
				<DialogTitle>ASIN list from Amazon affiliate</DialogTitle>
				<DialogContent sx={{ height: '70vh' }}>
					<AsyncView async={fragSvc.async}>
						{form.$asin.value
							.slice()
							.sort((a, b) => sortOrder[a.type] - sortOrder[b.type])
							.map(asin => {
								const item = Object.assign(
									{},
									form.initial?.asin.find(a => a.asin === asin.asin),
									asin,
								);
								return (
									<Row
										key={asin.asin}
										justifyContent="flex-start"
										alignItems="flex-start"
										borderBottom="solid 1px #808080"
										gap={2}
										py={1}
									>
										<img
											src={item?.images[0]}
											alt=""
											height={150}
											width={150}
											style={{ objectFit: 'contain' }}
										/>
										<Col py={1} flex={1}>
											<Text sx={{ textDecoration: 'underline' }}>
												<a href={item?.url} target="_blank" rel="noreferrer">
													{item?.title}
												</a>
											</Text>
											<Row gap={2}>
												<Col pl={2} flex={3}>
													<TitleValue title="ASIN: " value={asin.asin} />
													<TitleValue
														title="CreatedAt: "
														value={dayjs(item?.createdAt).format()}
													/>
													<TitleValue
														title="UpdatedAt: "
														value={dayjs(item?.updatedAt).format()}
													/>
													<TitleValue title="Type:" value={asin.type} />
												</Col>
												<Col flex={1} alignItems="flex-end">
													{(asin.type === 'PRIMARY' || asin.type === 'VARIATION') && (
														<Button
															variant="contained"
															color="warning"
															onClick={action(() => (asin.type = 'BLACKLIST'))}
														>
															TO BLACKLIST
														</Button>
													)}
													{asin.type === 'POTENTIAL' && (
														<Button
															variant="contained"
															color="success"
															onClick={action(() => (asin.type = 'MANUAL'))}
														>
															TO MANUAL
														</Button>
													)}
													{asin.type === 'MANUAL' && (
														<Button
															variant="contained"
															color="secondary"
															onClick={action(() => (asin.type = 'POTENTIAL'))}
														>
															TO POTENTIAL
														</Button>
													)}
													{asin.type === 'BLACKLIST' && (
														<Button
															variant="contained"
															color="primary"
															onClick={action(() => (asin.type = 'VARIATION'))}
														>
															TO VARIATION
														</Button>
													)}
												</Col>
											</Row>
										</Col>
									</Row>
								);
							})}
					</AsyncView>
				</DialogContent>
				<DialogActions>
					<Button variant="contained" onClick={handleRefetch}>
						Refetch from Amazon
					</Button>
					<Button onClick={ui.hideDialog}>Close</Button>
				</DialogActions>
			</Dialog>
		);
	},
);

const TitleValue = ({ title, value }: { title: ReactNode; value: ReactNode }) => {
	return (
		<Row gap={2}>
			<Text variant="body2" fontWeight="bold">
				{title}
			</Text>
			{typeof title === 'string' ? (
				<Text variant="body2" textAlign="right" flex={1}>
					{value}
				</Text>
			) : (
				value
			)}
		</Row>
	);
};
